import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import CurrencyFlag from 'react-currency-flags';
import ReactCountryFlag from 'react-country-flag';

import StyledSelect from '../../components/input/StyledSelect';
import StyledTextField from '../../components/input/StyledTextField';
import ChannelIcon from '../../components/ChannelIcon';
import { isEmpty, usePrevious } from '../../library/utils';
import {
  CountryCurrencies,
  CountryDialIsoFlags,
  DefactoCountryFlag,
} from '../../library/dssc-country-nationality-municipality-codes';

import { fetchCountriesFromLibrary } from '../../store/actions/location-actions';
// import { fetchRecipients } from '../../store/actions/recipient-actions';
import {
  computeRate,
  fetchLocalCountries,
  fetchRemitOptions,
  resetComputedRate,
  resetRemitOptions,
} from '../../store/actions/rate-actions';
import {
  requestQuote,
  resetQuoteResult,
} from '../../store/actions/quote-actions';
import { resetRemit } from '../../store/actions/remit-actions';
import { resetInitiatedTransaction } from '../../store/actions/transaction-actions';

// import { WhoWeAreImage, WhySendWithUsImage } from '../../assets/assetSelector';

import config from '../../config';
import homeStyles from './homeStyles';

const Home = (props) => {
  const dispatch = useDispatch();
  const styles = homeStyles();
  const search = useLocation().search;
  const searchParams = new URLSearchParams(search);
  const theme = useTheme();

  // Selectors
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const user = useSelector((state) => state.auth.user);
  const locationIsLoading = useSelector((state) => state.location.loading);
  const receivingCountriesFetched = useSelector(
    (state) => state.location.receivingCountriesFetched
  );
  const receivingCountries = useSelector(
    (state) => state.location.receivingCountries
  );
  // const recipientIsLoading = useSelector((state) => state.recipient.loading);
  // const recipients = useSelector((state) => state.recipient.recipients);
  const rateOptionsIsLoading = useSelector(
    (state) => state.rate.loadingRemitOptions
  );
  const localCountriesIsLoading = useSelector(
    (state) => state.rate.loadingLocalCountries
  );
  const localCountriesFetched = useSelector(
    (state) => state.location.localCountriesFetched
  );
  const localCountries = useSelector((state) => state.rate.localCountries);
  const rateOptions = useSelector((state) => state.rate.remitOptions);
  const errorComputedRate = useSelector(
    (state) => state.rate.errorComputedRate
  );
  const computedRate = useSelector((state) => state.rate.computedRate);
  const loadingComputedRate = useSelector(
    (state) => state.rate.loadingComputedRate
  );

  // States
  const [
    configServiceDefaultSendingCurrencies,
    setConfigServiceDefaultSendingCurrencies,
  ] = useState([]);
  const [
    configServiceDefaultReceivingCurrencies,
    setConfigServiceDefaultReceivingCurrencies,
  ] = useState([]);
  const [receivingCountryCode, setReceivingCountryCode] = useState('');
  const [receivingCountryIsoCode, setReceivingCountryIsoCode] = useState('');
  // const [recipientIndex, setRecipientIndex] = useState(
  //   searchParams.get('recipient')
  //     ? searchParams.get('recipient') !== 'new'
  //       ? parseInt(searchParams.get('recipient'))
  //       : 'new'
  //     : 'new'
  // ); // 1-based
  const [deliveryMethod, setDeliveryMethod] = useState(
    searchParams.get('method') || ''
  );
  const [amountValues, setAmountValues] = useState({
    sendAmount:
      ((searchParams.get('mode') === 'send' || !searchParams.get('mode')) &&
        searchParams.get('amount')) ||
      '',
    receiveAmount:
      (searchParams.get('mode') === 'receive' && searchParams.get('amount')) ||
      '',
    isSending:
      (searchParams.get('mode') && searchParams.get('mode') === 'send') ||
      !searchParams.get('mode'),
  });
  const [sendingCurrency, setSendingCurrency] = useState(
    searchParams.get('sendcurrency') || ''
  );
  const [receivingCurrency, setReceivingCurrency] = useState(
    searchParams.get('receivecurrency') || ''
  );
  const [receivingCountryOptions, setReceivingCountryOptions] = useState([]);
  // const [recipientOptions, setRecipientOptions] = useState([]);
  const [remitOptions, setRemitOptions] = useState({
    methods: {},
    methodOptions: [],
    products: {},
  });

  const prevReceivingCountryCode = usePrevious(receivingCountryCode);

  // Effects
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(resetQuoteResult());
    dispatch(resetRemit());
    dispatch(resetRemitOptions());
    dispatch(resetInitiatedTransaction());

    if (config.REACT_APP_QUOTE_SERVICE_DEFAULT_SENDING_CURRENCY) {
      const defaultSendingCurrencies = {};
      config.REACT_APP_SERVICES_WITH_BANK_BRANCHES.split(',').forEach((s) => {
        defaultSendingCurrencies[s.split('/')[0]] = s.split('/')[1];
      });
      setConfigServiceDefaultSendingCurrencies(defaultSendingCurrencies);
    }
    if (config.REACT_APP_QUOTE_SERVICE_DEFAULT_RECEIVING_CURRENCY) {
      const defaultReceivingCurrencies = {};
      config.REACT_APP_SERVICES_WITH_BANK_BRANCHES.split(',').forEach((s) => {
        defaultReceivingCurrencies[s.split('/')[0]] = s.split('/')[1];
      });
      setConfigServiceDefaultReceivingCurrencies(defaultReceivingCurrencies);
    }
    return () => {
      dispatch(resetComputedRate());
      // setRecipientIndex('new');
      setDeliveryMethod('');
      setAmountValues({ sendAmount: '', receiveAmount: '', isSending: true });
      setSendingCurrency('');
      setReceivingCurrency('');
    };
  }, [dispatch]);

  useEffect(() => {
    if (isEmpty(receivingCountries) && !receivingCountriesFetched) {
      // if (config.REACT_APP_FETCH_DESTINATION_COUNTRY_MODE === 'LIBRARY') {
      dispatch(fetchCountriesFromLibrary('RECEIVING'));
      // } else {
      //   dispatch(
      //     fetchCountries(
      //       config.REACT_APP_FETCH_DESTINATION_COUNTRY_BUSINESSCODE,
      //       'RECEIVING'
      //     )
      //   );
      // }
    } else if (
      config.REACT_APP_FX_RATE_MODE === 'LOCAL' &&
      isEmpty(localCountries) &&
      !localCountriesFetched
    ) {
      dispatch(fetchLocalCountries());
    } else if (
      !isEmpty(receivingCountries) &&
      (config.REACT_APP_FX_RATE_MODE !== 'LOCAL' ||
        (config.REACT_APP_FX_RATE_MODE === 'LOCAL' && !isEmpty(localCountries)))
    ) {
      const filteredCountries = [];
      const countryOptions = [];

      receivingCountries.forEach((c) => {
        let localCountry = null;

        if (localCountries.indexOf(c.id.toString()) > -1) {
          localCountry = c.id.toString();
        } else if (localCountries.indexOf(c.code.toString()) > -1) {
          localCountry = c.code.toString();
        } else if (localCountries.indexOf(c.isoCode.toString()) > -1) {
          localCountry = c.isoCode.toString();
        }
        if (config.REACT_APP_FX_RATE_MODE !== 'LOCAL' || localCountry) {
          filteredCountries.push({ ...c });
          const country = CountryDialIsoFlags.find(
            (cif) => cif.isoCode === c.isoCode
          );
          countryOptions.push({
            value: localCountry,
            display: country.name,
            listItemIcon:
              c.isoCode.toUpperCase() === 'XX' ? (
                <img
                  style={{
                    display: 'inline-block',
                    width: '25px',
                    height: '25px',
                    verticalAlign: 'middle',
                  }}
                  alt={c.isoCode.toUpperCase()}
                  src={DefactoCountryFlag('xx')}
                />
              ) : (
                <ReactCountryFlag
                  svg
                  countryCode={c.isoCode.toUpperCase()}
                  style={{ height: '25px', width: '25px' }}
                />
              ),
          });
        }
      });

      setReceivingCountryOptions(countryOptions);

      const paramCountry =
        props.match.params.countryCode &&
        filteredCountries.find(
          (c) =>
            c.id.toString().toUpperCase() ===
              props.match.params.countryCode.toUpperCase() ||
            (props.match.params.countryCode.length === 2 &&
              c.isoCode.toString().toUpperCase() ===
                props.match.params.countryCode.toUpperCase()) ||
            (props.match.params.countryCode.length > 2 &&
              c.countryName
                .split(' ')
                .join('')
                .toUpperCase()
                .includes(
                  props.match.params.countryCode
                    .split(' ')
                    .join('')
                    .toUpperCase()
                ))
        );

      if (paramCountry) {
        if (localCountries.indexOf(paramCountry.id.toString()) > -1) {
          setReceivingCountryCode(paramCountry.id.toString());
        } else if (localCountries.indexOf(paramCountry.code.toString()) > -1) {
          setReceivingCountryCode(paramCountry.code.toString());
        } else if (
          localCountries.indexOf(paramCountry.isoCode.toString()) > -1
        ) {
          setReceivingCountryCode(paramCountry.isoCode.toString());
        }

        setReceivingCountryIsoCode(paramCountry.isoCode);
      } else {
        props.history.push(
          '/remit/' +
            filteredCountries[0].countryName.split(' ').join('').toLowerCase() +
            props.location.search
        );
      }
    }
  }, [
    receivingCountries,
    receivingCountriesFetched,
    localCountries,
    localCountriesFetched,
    props.match.params.countryCode,
    props.history,
    props.location,
    dispatch,
  ]);

  // useEffect(() => {
  //   if (isLoggedIn && isEmpty(recipients)) {
  //     dispatch(fetchRecipients());
  //   } else if (isLoggedIn && !isEmpty(recipients)) {
  //     setRecipientOptions([
  //       {
  //         value: 'new',
  //         display: 'New Recipient',
  //       },
  //       ...recipients
  //         .filter((r) => r.country === receivingCountryCode)
  //         .map((r, index) => {
  //           return {
  //             value: index + 1,
  //             display: `${r.firstname} ${r.lastname}`,
  //           };
  //         }),
  //     ]);
  //     setRecipientIndex(1);
  //   }
  // }, [recipients, receivingCountryCode, isLoggedIn, dispatch]);

  useEffect(() => {
    if (
      isEmpty(rateOptions) &&
      prevReceivingCountryCode !== receivingCountryCode &&
      (config.REACT_APP_FX_RATE_MODE !== 'LOCAL' ||
        (config.REACT_APP_FX_RATE_MODE === 'LOCAL' && receivingCountryCode))
    ) {
      dispatch(
        fetchRemitOptions(
          config.REACT_APP_FX_RATE_MODE === 'LOCAL'
            ? receivingCountryCode
            : null
        )
      );
    } else if (
      !isEmpty(rateOptions) &&
      rateOptions.services?.some((s) => s.code === deliveryMethod)
    ) {
      const remitOptions = {
        methods: {},
        methodOptions: [],
        products: {},
      };

      if (!isEmpty(rateOptions.services)) {
        rateOptions.services.forEach((s) => {
          remitOptions.methods[s.code] = {
            ...s,
            channelId: parseInt(s.channel),
          };
          remitOptions.methodOptions.push({
            value: s.code,
            display: s.name,
            listItemIcon: <ChannelIcon channel={s.channel} />,
          });
        });
      }

      if (!isEmpty(rateOptions.products)) {
        rateOptions.products.forEach((p) => {
          remitOptions.products[p.id] = {
            ...p,
            sendingCurrencies: p.sendingCurrencies.map((c) => {
              return {
                value: c.code,
                display: c.code,
                listItemIcon: <CurrencyFlag currency={c.code} />,
              };
            }),
            receivingCurrencies: p.receivingCurrencies.map((c) => {
              return {
                value: c.code,
                display: c.code,
                listItemIcon: <CurrencyFlag currency={c.code} />,
              };
            }),
          };
        });

        if (
          deliveryMethod &&
          !sendingCurrency &&
          remitOptions.products[
            remitOptions.methods[deliveryMethod].productId
          ].sendingCurrencies.some(
            (c) =>
              c.value ===
              configServiceDefaultSendingCurrencies[
                remitOptions.methods[deliveryMethod].id.toUpperCase()
              ]
          )
        ) {
          setSendingCurrency(
            configServiceDefaultSendingCurrencies[
              remitOptions.methods[deliveryMethod].id.toUpperCase()
            ]
          );
        } else if (
          deliveryMethod &&
          !sendingCurrency &&
          remitOptions.products[
            remitOptions.methods[deliveryMethod].productId
          ].sendingCurrencies.some(
            (c) =>
              c.value ===
              CountryCurrencies[config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE]
          )
        ) {
          setSendingCurrency(
            CountryCurrencies[config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE]
          );
        } else if (
          deliveryMethod &&
          remitOptions.products[
            remitOptions.methods[deliveryMethod].productId
          ].sendingCurrencies.find((c) => c.value === sendingCurrency)
        ) {
          // do nothing
        } else {
          setSendingCurrency(
            remitOptions.products[
              remitOptions.methods[deliveryMethod].productId
            ].sendingCurrencies[0].value
          );
        }

        if (
          deliveryMethod &&
          !receivingCurrency &&
          remitOptions.products[
            remitOptions.methods[deliveryMethod].productId
          ].receivingCurrencies.some(
            (c) =>
              c.value ===
              configServiceDefaultReceivingCurrencies[
                remitOptions.methods[deliveryMethod].id.toUpperCase()
              ]
          )
        ) {
          setReceivingCurrency(
            configServiceDefaultReceivingCurrencies[
              remitOptions.methods[deliveryMethod].id.toUpperCase()
            ]
          );
        } else if (
          deliveryMethod &&
          !receivingCurrency &&
          remitOptions.products[
            remitOptions.methods[deliveryMethod].productId
          ].receivingCurrencies.some(
            (c) => c.value === CountryCurrencies[receivingCountryIsoCode]
          )
        ) {
          setReceivingCurrency(CountryCurrencies[receivingCountryIsoCode]);
        } else if (
          deliveryMethod &&
          remitOptions.products[
            remitOptions.methods[deliveryMethod].productId
          ].receivingCurrencies.find((c) => c.value === receivingCurrency)
        ) {
          // Do nothing
        } else {
          setReceivingCurrency(
            remitOptions.products[
              remitOptions.methods[deliveryMethod].productId
            ].receivingCurrencies[0].value
          );
        }
      }

      setRemitOptions(remitOptions);
    } else if (
      !isEmpty(rateOptions) &&
      !isEmpty(rateOptions.services) &&
      !rateOptions.services?.some((s) => s.code === deliveryMethod)
    ) {
      setDeliveryMethod(rateOptions.services[0].code);
    }
  }, [
    rateOptions,
    prevReceivingCountryCode,
    receivingCountryCode,
    receivingCountryIsoCode,
    deliveryMethod,
    sendingCurrency,
    receivingCurrency,
    configServiceDefaultSendingCurrencies,
    configServiceDefaultReceivingCurrencies,
    dispatch,
  ]);

  useEffect(() => {
    if (computedRate.marketFx) {
      setAmountValues({
        // sendAmount: computedRate.senderAmount
        //   .toLocaleString(navigator.language, { minimumFractionDigits: 2 })
        //   .replace(/,/g, ''),
        // receiveAmount: computedRate.receiverAmount
        //   .toLocaleString(navigator.language, { minimumFractionDigits: 2 })
        //   .replace(/,/g, ''),
        sendAmount: parseFloat(computedRate.senderAmount).toFixed(2),
        receiveAmount: parseFloat(computedRate.receiverAmount).toFixed(2),
        isSending: computedRate.isSending,
      });
    }
  }, [computedRate]);

  useEffect(() => {
    let submitQuoteTimer = null;

    if (
      remitOptions.methods[deliveryMethod]?.productId &&
      receivingCurrency &&
      sendingCurrency &&
      ((amountValues.sendAmount && !amountValues.receiveAmount) ||
        (!amountValues.sendAmount && amountValues.receiveAmount))
    ) {
      let countryCode =
        config.REACT_APP_FX_RATE_MODE === 'LOCAL' ? receivingCountryCode : null;

      submitQuoteTimer = setTimeout(() => {
        if (isLoggedIn) {
          dispatch(
            requestQuote(
              {
                product: remitOptions.methods[deliveryMethod].productId,
                channelid: remitOptions.methods[deliveryMethod].channelId,
                sendingcurrency: sendingCurrency,
                receivingcurrency: receivingCurrency,
                serviceid: remitOptions.methods[deliveryMethod].id,
                issending: amountValues.isSending,
                amount: amountValues.isSending
                  ? amountValues.sendAmount
                  : amountValues.receiveAmount,
                receivingCountryCode: countryCode,
                businessCode:
                  remitOptions.products[
                    remitOptions.methods[deliveryMethod].productId
                  ]?.businessCode,
              },
              countryCode,
              true,
              user.id
            )
          );
        } else {
          dispatch(
            computeRate(
              remitOptions.methods[deliveryMethod].productId,
              remitOptions.methods[deliveryMethod].id,
              sendingCurrency,
              receivingCurrency,
              amountValues.isSending
                ? amountValues.sendAmount
                : amountValues.receiveAmount,
              amountValues.isSending,
              countryCode,
              remitOptions.methods[deliveryMethod].channelId,
              remitOptions.products[
                remitOptions.methods[deliveryMethod].productId
              ]?.businessCode,
              isLoggedIn,
              user.id
            )
          );
        }
      }, 1000);
    }

    return () => {
      clearTimeout(submitQuoteTimer);
    };
  }, [remitOptions, deliveryMethod, receivingCurrency, sendingCurrency, amountValues, receivingCountryCode, isLoggedIn, user, dispatch]);

  useEffect(() => {
    if (
      deliveryMethod &&
      !isEmpty(remitOptions.methods) &&
      !isEmpty(remitOptions.products) &&
      !isEmpty(remitOptions.methods[deliveryMethod])
    ) {
      if (!sendingCurrency) {
        const defaultSendingCurrency =
          configServiceDefaultSendingCurrencies[
            remitOptions.methods[deliveryMethod].id.toUpperCase()
          ];

        if (
          defaultSendingCurrency &&
          remitOptions.products[
            remitOptions.methods[deliveryMethod].productId
          ]?.sendingCurrencies.some((sc) => sc.value === defaultSendingCurrency)
        ) {
          setSendingCurrency(defaultSendingCurrency);
        } else if (
          CountryCurrencies[config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE] &&
          remitOptions.products[
            remitOptions.methods[deliveryMethod].productId
          ]?.sendingCurrencies.some(
            (sc) =>
              sc.value ===
              CountryCurrencies[config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE]
          )
        ) {
          setSendingCurrency(
            CountryCurrencies[config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE]
          );
        } else {
          setSendingCurrency(
            remitOptions.products[
              remitOptions.methods[deliveryMethod].productId
            ].sendingCurrencies[0].value
          );
        }
      }

      if (!receivingCurrency) {
        const defaultReceivingCurrency =
          configServiceDefaultReceivingCurrencies[
            remitOptions.methods[deliveryMethod].id.toUpperCase()
          ];

        if (
          defaultReceivingCurrency &&
          remitOptions.products[
            remitOptions.methods[deliveryMethod].productId
          ]?.receivingCurrencies.some(
            (sc) => sc.value === defaultReceivingCurrency
          )
        ) {
          setReceivingCurrency(defaultReceivingCurrency);
        } else if (
          CountryCurrencies[receivingCountryCode] &&
          remitOptions.products[
            remitOptions.methods[deliveryMethod].productId
          ]?.receivingCurrencies.some(
            (sc) => sc.value === CountryCurrencies[receivingCountryCode]
          )
        ) {
          setReceivingCurrency(CountryCurrencies[receivingCountryCode]);
        } else {
          setReceivingCurrency(
            remitOptions.products[
              remitOptions.methods[deliveryMethod].productId
            ].receivingCurrencies[0].value
          );
        }
      }
    }
  }, [
    deliveryMethod,
    remitOptions,
    sendingCurrency,
    receivingCurrency,
    receivingCountryCode,
    configServiceDefaultSendingCurrencies,
    configServiceDefaultReceivingCurrencies,
  ]);

  useEffect(() => {
    // ${isLoggedIn ? '&recipient=' + recipientIndex : ''}
    props.history.push(
      `/remit/${
        props.match.params.countryCode
      }?method=${deliveryMethod}&sendcurrency=${sendingCurrency}&receivecurrency=${receivingCurrency}&amount=${
        amountValues.isSending
          ? amountValues.sendAmount
          : amountValues.receiveAmount
      }&mode=${amountValues.isSending ? 'send' : 'receive'}`
    );
  }, [
    deliveryMethod,
    // recipientIndex,
    sendingCurrency,
    receivingCurrency,
    amountValues,
    props.match.params.countryCode,
    props.history,
    isLoggedIn,
  ]);

  // handlers
  const handleReceivingCountryChange = (e) => {
    dispatch(resetRemitOptions());
    dispatch(resetComputedRate());
    setDeliveryMethod('');
    setSendingCurrency('');
    setReceivingCurrency('');
    setAmountValues({
      sendAmount: '',
      receiveAmount: '',
      isSending: false,
    });

    const paramCountry = receivingCountries.find(
      (c) =>
        c.id.toString() === e.target.value ||
        c.code.toString() === e.target.value ||
        c.isoCode.toString() === e.target.value
    );

    props.history.push(
      '/remit/' +
        paramCountry.countryName.split(' ').join('').toLowerCase() +
        props.location.search
    );
  };

  const handleDeliveryMethodChange = (e) => {
    dispatch(resetComputedRate());
    setSendingCurrency('');
    setReceivingCurrency('');
    setAmountValues({
      sendAmount: '',
      receiveAmount: '',
      isSending: false,
    });

    setDeliveryMethod(e.target.value);
  };

  const handleSendingCurrencyChange = (e) => {
    dispatch(resetComputedRate());
    setAmountValues({
      sendAmount: '',
      receiveAmount: '',
      isSending: false,
    });

    setSendingCurrency(e.target.value);
  };

  const handleReceivingCurrencyChange = (e) => {
    dispatch(resetComputedRate());
    setAmountValues({
      sendAmount: '',
      receiveAmount: '',
      isSending: false,
    });

    setReceivingCurrency(e.target.value);
  };

  const handleAmountChange = (e) => {
    dispatch(resetComputedRate());

    if (e.target.id === 'you-send') {
      setAmountValues({
        sendAmount: e.target.value,
        receiveAmount: '',
        isSending: true,
      });
    } else if (e.target.id === 'recipient-gets') {
      setAmountValues({
        sendAmount: '',
        receiveAmount: e.target.value,
        isSending: false,
      });
    }
  };

  // render
  return (
    <>
      <div
        className={`${styles.root} ${
          isLoggedIn ? styles.rootLoggedIn : styles.rootLoggedOut
        }`}
      >
        <div className={styles.sendMoneyDiv}>
          <Typography
            component="span"
            variant="body4"
            color="secondary"
            sx={{
              position: 'relative',
              bottom: `calc(-500px + ${
                theme.config.isTopNavbarFixed ? '0px' : '96px'
              })`,
            }}
          >
            {config.REACT_APP_MAIN_CATCHPHRASE}
          </Typography>
        </div>
        <div className={styles.marketingDiv}>
          <StyledEngineProvider injectFirst>
            <Card className={styles.sendMoneyCard}>
              <Box>
                <CardContent>
                  <StyledSelect
                    id="receiving-country"
                    label="Sending to"
                    options={receivingCountryOptions}
                    listItemTextTypographyStyle={{ fontWeight: 'bold' }}
                    formControlVariant="outlined"
                    fullWidth
                    listItemIcon
                    isLoading={locationIsLoading || localCountriesIsLoading}
                    value={receivingCountryCode}
                    handleChange={handleReceivingCountryChange}
                  />
                  {/* {isLoggedIn && (
                    <StyledSelect
                      id="recipient"
                      label="Recipient"
                      options={recipientOptions}
                      listItemTextTypographyStyle={{ fontWeight: 'bold' }}
                      formControlVariant="outlined"
                      fullWidth
                      isLoading={recipientIsLoading}
                      value={recipientIndex}
                      handleChange={(e) => setRecipientIndex(e.target.value)}
                    />
                  )} */}
                  <StyledSelect
                    id="delivery-method"
                    label="Delivery Method"
                    options={remitOptions.methodOptions}
                    listItemTextTypographyStyle={{ fontWeight: 'bold' }}
                    formControlVariant="outlined"
                    fullWidth
                    listItemIcon
                    isLoading={rateOptionsIsLoading}
                    value={deliveryMethod}
                    handleChange={handleDeliveryMethodChange}
                  />
                  <StyledTextField
                    id="recipient-gets"
                    label="Recipient will receive"
                    placeholder="0.00"
                    type="number"
                    textFieldVariant="outlined"
                    startAdornment={
                      remitOptions.products[
                        remitOptions.methods[
                          deliveryMethod || Object.keys(remitOptions.methods)[0]
                        ]?.productId
                      ]?.receivingCurrencies?.length > 1 ? (
                        <StyledSelect
                          id="recipient-gets-currency"
                          options={
                            remitOptions.products[
                              remitOptions.methods[
                                deliveryMethod ||
                                  Object.keys(remitOptions.methods)[0]
                              ]?.productId
                            ]?.receivingCurrencies
                          }
                          disableUnderline
                          fullWidth
                          noLabel
                          noFormControl
                          noOutline
                          noBottomMargin
                          listItemIcon
                          listItemTextSx={{ width: '35px' }}
                          isLoading={rateOptionsIsLoading}
                          value={receivingCurrency}
                          handleChange={handleReceivingCurrencyChange}
                        />
                      ) : (
                        <MenuItem
                          classes={{
                            root: styles.currencyFlagMenuItemRoot,
                          }}
                        >
                          {receivingCurrency && (
                            <ListItemIcon>
                              <CurrencyFlag currency={receivingCurrency} />
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={
                              <Typography>{receivingCurrency}</Typography>
                            }
                            sx={{ width: '35px' }}
                          />
                        </MenuItem>
                      )
                    }
                    endAdornment={
                      loadingComputedRate && (
                        <Box sx={{ width: '25px' }}>
                          <LinearProgress />
                        </Box>
                      )
                    }
                    fullWidth
                    currency
                    noLeftPaddingTextField
                    value={amountValues.receiveAmount}
                    handleChange={handleAmountChange}
                    error={
                      amountValues.receiveAmount?.toString() &&
                      amountValues.receiveAmount < 1
                    }
                    errorMessage={
                      amountValues.receiveAmount?.toString() &&
                      amountValues.receiveAmount < 1 &&
                      'Amount must be greater than 1'
                    }
                  />
                  <StyledTextField
                    id="you-send"
                    label="You will send"
                    placeholder="0.00"
                    type="number"
                    textFieldVariant="outlined"
                    startAdornment={
                      remitOptions.products[
                        remitOptions.methods[
                          deliveryMethod || Object.keys(remitOptions.methods)[0]
                        ]?.productId
                      ]?.sendingCurrencies?.length > 1 ? (
                        <StyledSelect
                          id="you-send-currency"
                          options={
                            remitOptions.products[
                              remitOptions.methods[
                                deliveryMethod ||
                                  Object.keys(remitOptions.methods)[0]
                              ]?.productId
                            ]?.sendingCurrencies
                          }
                          disableUnderline
                          fullWidth
                          noLabel
                          noFormControl
                          noOutline
                          noBottomMargin
                          listItemIcon
                          listItemTextSx={{ width: '35px' }}
                          isLoading={rateOptionsIsLoading}
                          value={sendingCurrency}
                          handleChange={handleSendingCurrencyChange}
                        />
                      ) : (
                        <MenuItem
                          classes={{
                            root: styles.currencyFlagMenuItemRoot,
                          }}
                        >
                          {sendingCurrency && (
                            <ListItemIcon>
                              <CurrencyFlag currency={sendingCurrency} />
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={<Typography>{sendingCurrency}</Typography>}
                            sx={{ width: '35px' }}
                          />
                        </MenuItem>
                      )
                    }
                    endAdornment={
                      loadingComputedRate && (
                        <Box sx={{ width: '25px' }}>
                          <LinearProgress />
                        </Box>
                      )
                    }
                    fullWidth
                    currency
                    noLeftPaddingTextField
                    value={amountValues.sendAmount}
                    handleChange={handleAmountChange}
                    error={
                      amountValues.sendAmount?.toString() &&
                      amountValues.sendAmount < 1
                    }
                    errorMessage={
                      amountValues.sendAmount?.toString() &&
                      amountValues.sendAmount < 1 &&
                      'Amount must be greater than 1'
                    }
                  />
                  <Collapse in={computedRate.marketFx && true}>
                    <Box mb={1}>
                      <Alert severity="success">
                        {`1 ${sendingCurrency} = ${computedRate.marketFx?.toLocaleString(
                          navigator.language,
                          { minimumFractionDigits: 4 }
                        )} ${receivingCurrency}`}
                      </Alert>
                    </Box>
                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      color="secondary"
                      type="button"
                      component={Link}
                      to={
                        '/remit/quote/' +
                        props.match.params.countryCode +
                        props.location.search
                      }
                      disabled={
                        !isEmpty(amountValues) &&
                        (amountValues.sendAmount < 1 ||
                          amountValues.receiveAmount < 1)
                      }
                    >
                      Continue
                    </Button>
                  </Collapse>
                  <Collapse in={errorComputedRate && true}>
                    <Alert severity="info">
                      {`This is embarassing. ${sendingCurrency} to ${receivingCurrency} will be available soon.`}
                    </Alert>
                  </Collapse>
                </CardContent>
              </Box>
            </Card>
          </StyledEngineProvider>
        </div>
      </div>
      {/* <div className={styles.divMarketing}>
        <img src={WhySendWithUsImage()} alt="Why send with us" width="80%" />
      </div>
      <div className={styles.divMarketing}>
        <img src={WhoWeAreImage()} alt="Who we are" width="80%" />
      </div> */}
    </>
  );
};

export default Home;
